<template>
  <!--
    Eine Trefferzeile (Kundenfeedback 18.10.2026, XII): Name/Firma, Ort und
    E-Mail, daneben die Herkunft als Text-Badge („Eigener Kontakt“ / „In KARS
    registriert“ / „Versicherungsverzeichnis“), Stern = Favorit. „Zuordnen“
    öffnet die Rollenwahl (vorbelegt, frei änderbar, jede Rolle erlaubt).
    Im Reiter „Favoriten“ ordnet ein Klick direkt der Standardrolle zu.
    Die Zeile lässt sich auf eine Rolle links ziehen.
  -->
  <li
    class="crr"
    :class="{ 'crr--open': open, 'crr--in-case': inCase, 'crr--dragging': dragging }"
    :draggable="!inCase && !open"
    :data-testid="'apd-hit-' + hit.key"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
  >
    <div class="crr__main">
      <button
        v-if="hit.source === 'own'"
        type="button"
        class="crr__star"
        :class="{ 'crr__star--on': hit.isFavorite }"
        :aria-pressed="hit.isFavorite"
        :aria-label="hit.isFavorite ? $gt('Aus den Favoriten entfernen') : $gt('Als Favorit merken')"
        :title="hit.isFavorite ? $gt('Aus den Favoriten entfernen') : $gt('Als Favorit merken')"
        :disabled="busy"
        data-testid="apd-hit-star"
        @click="$emit('toggle-favorite', hit)"
      >
        <Star :size="15" aria-hidden="true" />
      </button>
      <span v-else class="crr__star-spacer" aria-hidden="true" />

      <div class="crr__text">
        <div class="crr__line">
          <span class="crr__name">{{ hit.name }}</span>
          <span v-if="hit.detail" class="crr__detail">{{ hit.detail }}</span>
        </div>
        <div class="crr__line crr__line--sub">
          <span v-if="hit.city" class="crr__city">{{ hit.city }}</span>
          <span v-if="hit.email" class="crr__email">{{ hit.email }}</span>
          <span v-if="!hit.city && !hit.email" class="crr__muted">{{ $gt('Keine Adresse hinterlegt') }}</span>
        </div>
      </div>

      <div class="crr__badges">
        <span class="crr__badge" :class="'crr__badge--' + hit.source">{{ $gt(sourceLabel) }}</span>
        <span v-if="hit.source === 'own' && hit.isPortalLinked" class="crr__badge crr__badge--registered">{{ $gt('In KARS registriert') }}</span>
        <!-- Kundenfeedback 02.11.2026 (I): wird ohne Einladung zugeordnet;
             03.11.2026: „per E-Mail abgemeldet“, wenn die Adresse den Abmeldelink genutzt hat -->
        <span
          v-if="hit.refusesInvitations"
          class="crr__badge crr__badge--no-invite"
          data-testid="apd-hit-no-invitations"
          :title="hit.optedOutByMailLabel ? `${$gt('Wird ohne KARS-Einladung in der Akte geführt')} (${hit.optedOutByMailLabel}).` : $gt('Wird ohne KARS-Einladung in der Akte geführt.')"
        >{{ hit.optedOutByMailLabel ? $gt('Per E-Mail abgemeldet') : $gt('Möchte keine KARS-Einladungen') }}</span>
        <span v-if="inCase" class="crr__badge crr__badge--case">{{ $gt('Bereits in der Akte') }}</span>
      </div>

      <div class="crr__actions">
        <template v-if="favoriteMode && !inCase">
          <button type="button" class="crr__assign" :disabled="busy" data-testid="apd-hit-quick" @click="emitAssign(favoriteTargetRole)">
            {{ $gt('Als') }} {{ roleLabelOf(favoriteTargetRole) }} {{ $gt('zuordnen') }}
          </button>
          <button type="button" class="crr__more" :disabled="busy" :title="$gt('Andere Rolle wählen')" @click="toggleOpen">
            {{ $gt('Andere Rolle') }}
          </button>
        </template>
        <button
          v-else-if="!inCase"
          type="button"
          class="crr__assign"
          :class="{ 'crr__assign--ghost': open }"
          :aria-expanded="open"
          :disabled="busy"
          data-testid="apd-hit-assign"
          @click="toggleOpen"
        >
          {{ open ? $gt('Abbrechen') : $gt('Zuordnen') }}
        </button>
      </div>
    </div>

    <div v-if="open" class="crr__panel" data-testid="apd-assign-panel">
      <label class="crr__field">
        <span class="crr__field-label">{{ $gt('Zuordnen als') }}</span>
        <select v-model="role" class="crr__select" data-testid="apd-assign-role">
          <option v-for="r in roleChoices" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>
      </label>
      <SelectionNotificationToggle
        v-if="role !== mandantKey"
        :name="hit.name"
        :muted="!notify"
        :case-muted="caseMuted"
        :insurance="isInsuranceRole"
        @toggle="notify = !notify"
      />
      <span class="crr__panel-spacer" />
      <button type="button" class="crr__assign" :disabled="busy" data-testid="apd-assign-confirm" @click="emitAssign(role)">
        <v-progress-circular v-if="busy" indeterminate size="12" width="2" color="white" />
        {{ $gt('Zuordnen') }}
      </button>
    </div>
  </li>
</template>

<script>
import { Star } from "lucide-vue-next";
import { hitSourceLabel } from "@/utils/contactSearchHits";
import SelectionNotificationToggle from "../SelectionNotificationToggle.vue";
import { MANDANT_ROLE_KEY, normalizeRole, presetRoleForHit, roleLabel } from "./participantDialogModel";

const INSURANCE_ROLES = new Set(["Versicherung", "Versicherungsmakler"]);

export default {
  name: "ContactResultRow",

  components: { Star, SelectionNotificationToggle },

  props: {
    /** ContactSearchHit */
    hit: { type: Object, required: true },
    roleChoices: { type: Array, default: () => [] },
    /** Links gewählte Rolle – hat Vorrang vor dem Kontakttyp */
    presetRole: { type: String, default: "" },
    inCase: { type: Boolean, default: false },
    /** Reiter „Favoriten“: ein Klick ordnet der Standardrolle zu */
    favoriteMode: { type: Boolean, default: false },
    caseMuted: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },

  emits: ["assign", "toggle-favorite", "drag-start", "drag-end"],

  data() {
    return { open: false, role: "", notify: true, dragging: false, mandantKey: MANDANT_ROLE_KEY };
  },

  computed: {
    sourceLabel() { return hitSourceLabel(this.hit); },
    isInsuranceRole() { return INSURANCE_ROLES.has(this.role) || this.hit.source === "insurance"; },
    favoriteTargetRole() { return normalizeRole(this.hit.favoriteRole || this.hit.defaultRole); },
  },

  methods: {
    roleLabelOf(role) { return roleLabel(role); },
    toggleOpen() {
      if (this.open) { this.open = false; return; }
      this.role = presetRoleForHit(this.hit, this.presetRole);
      this.notify = true;
      this.open = true;
    },
    emitAssign(role) {
      this.$emit("assign", { hit: this.hit, role, notify: this.notify });
      this.open = false;
    },
    onDragStart(event) {
      if (this.inCase) { event.preventDefault(); return; }
      this.dragging = true;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.setData("text/plain", this.hit.name);
      }
      this.$emit("drag-start", this.hit);
    },
    onDragEnd() {
      this.dragging = false;
      this.$emit("drag-end");
    },
  },
};
</script>

<style scoped>
.crr {
  list-style: none; border: 1px solid var(--border-grey-color-light); border-radius: 11px; background: #fff;
  transition: border-color var(--duration-fast, 150ms) ease, box-shadow var(--duration-fast, 150ms) ease, opacity var(--duration-fast, 150ms) ease;
}
.crr + .crr { margin-top: 6px; }
.crr[draggable="true"] { cursor: grab; }
.crr:hover { border-color: var(--border-grey-color); box-shadow: 0 2px 10px rgba(15, 23, 42, .05); }
.crr--open { border-color: var(--primary-color); box-shadow: 0 0 0 2px var(--primary-light-color-transparent); }
.crr--dragging { opacity: .55; }
.crr--in-case { background: var(--grey-light-color); }

.crr__main { display: flex; align-items: center; gap: 10px; padding: 9px 12px 9px 8px; }
.crr__star, .crr__star-spacer { flex-shrink: 0; width: 28px; height: 28px; }
.crr__star {
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 8px; background: none; color: #c0c6cf; cursor: pointer;
}
.crr__star:hover { background: var(--grey-light-color); color: #d4a106; }
.crr__star--on { color: #d4a106; }
.crr__star--on :deep(svg) { fill: currentColor; }
.crr__star:focus-visible { outline: 2px solid var(--primary-color); }

.crr__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.crr__line { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
.crr__name { font-size: 13.5px; font-weight: 650; color: var(--dark-color); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.crr__detail { font-size: 12px; color: var(--grey-color); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.crr__line--sub { font-size: 12px; color: #4b5563; }
.crr__city { white-space: nowrap; }
.crr__email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--grey-color); }
.crr__city + .crr__email::before { content: "·"; margin-right: 8px; color: var(--disabled-grey-color); }
.crr__muted { color: var(--disabled-grey-color); font-style: italic; }

.crr__badges { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 4px; max-width: 220px; }
.crr__badge {
  padding: 2px 8px; border-radius: 999px; border: 1px solid transparent;
  font-size: 11px; font-weight: 600; white-space: nowrap; line-height: 1.5;
}
/* Kundenfeedback 24.10.2026 (XIX/XX): weniger Farben – alle Herkunfts-Badges
   weiß mit dünnem Rand; nur „In KARS registriert“ trägt blaue Schrift. */
.crr__badge { background: #fff; border-color: var(--border-grey-color); color: var(--grey-color); }
.crr__badge--registered { color: var(--primary-color); }
.crr__badge--no-invite { border-color: #f5c26b; background: #fff8eb; color: #92400e; }

.crr__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.crr__assign {
  display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px;
  border: none; border-radius: 8px; background: var(--primary-color); color: #fff;
  font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: background-color var(--duration-fast, 150ms) ease, transform var(--duration-fast, 150ms) ease;
}
.crr__assign:hover:not(:disabled) { background: #0a5fc0; }
.crr__assign:active:not(:disabled) { transform: scale(.97); }
.crr__assign:disabled { opacity: .5; cursor: not-allowed; }
.crr__assign:focus-visible, .crr__more:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.crr__assign--ghost { background: #fff; color: var(--grey-color); box-shadow: inset 0 0 0 1px var(--border-grey-color); }
.crr__assign--ghost:hover:not(:disabled) { background: var(--grey-light-color); color: var(--dark-color); }
.crr__more {
  height: 30px; padding: 0 10px; border: 1px solid var(--border-grey-color); border-radius: 8px; background: #fff;
  font: inherit; font-size: 12px; font-weight: 600; color: var(--grey-color); cursor: pointer; white-space: nowrap;
}
.crr__more:hover { color: var(--primary-color); border-color: var(--primary-color); }

.crr__panel {
  display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
  padding: 10px 12px 10px 46px; border-top: 1px dashed var(--border-grey-color-light); background: #fafbfc;
  border-radius: 0 0 11px 11px;
}
.crr__field { display: inline-flex; align-items: center; gap: 8px; }
.crr__field-label { font-size: 12.5px; font-weight: 600; color: var(--dark-color); }
.crr__select {
  height: 30px; min-width: 200px; padding: 0 8px; border: 1px solid var(--border-grey-color); border-radius: 8px;
  background: #fff; font: inherit; font-size: 12.5px; color: var(--dark-color); cursor: pointer;
}
.crr__select:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px var(--primary-light-color-transparent); }
.crr__panel-spacer { flex: 1; }

@media (max-width: 760px) {
  .crr__main { flex-wrap: wrap; }
  .crr__badges { max-width: none; justify-content: flex-start; }
  .crr__panel { padding-left: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .crr, .crr__assign { transition: none; }
}
</style>
